import Link from 'next/link'
import * as React from 'react'
import { useState } from 'react'

import { resolveConsultationUrl } from '@/lib/tenant-media-defaults'
import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'
import { useTenantConfig } from '@/hooks/use-tenant-config'

import { Button, ButtonProps } from '@/components/ui/button'

import { isEnrollCtaLabel, TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'
import { FORM_REGISTER_URL } from '@/constant/app'
import { useAuth } from '@/contexts/auth-context'

interface ButtonRegisterProps {
  className?: string
  text?: string
  size?: ButtonProps['size']
  exluceBuyNowButton?: boolean
}

export default function ButtonRegister({
  className,
  size = 'lg',
  exluceBuyNowButton,
  text: buttonText,
}: ButtonRegisterProps) {
  const { text, lang } = useLocale()
  const [isCourseEnrolled, setIsCourseEnrolled] = useState(false)
  const { user } = useAuth()
  const { isFsdsDomain } = useBranding()
  const { customization } = useTenantConfig()
  const defaultLabel = text.MAIN.INTRODUCTION.HEADING_4
  const displayText = buttonText ?? defaultLabel

  React.useEffect(() => {
    if (user) {
      const courseSlug = user?.courseUsers?.map(courseUser => courseUser.course.slug)
      if (courseSlug) setIsCourseEnrolled(courseSlug.some(slug => slug.includes('mlops-1')))
    }
  }, [user])

  if (!isFsdsDomain) {
    const href = isEnrollCtaLabel(displayText)
      ? TEMPLATE_DEFAULT_URLS.myCourses
      : resolveConsultationUrl(customization)

    if (!href) {
      return (
        <Button variant="default" size={size} className={cn('text-base text-white', className)} disabled>
          {displayText}
        </Button>
      )
    }

    const isExternal = href.startsWith('http')

    return (
      <Button variant="default" size={size} className={cn('text-base text-white', className)} asChild>
        <Link
          href={href}
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {displayText}
        </Link>
      </Button>
    )
  }

  return (
    <Button variant="default" size={size} className={cn('text-base text-white', className)} asChild>
      {lang === 'vi' || exluceBuyNowButton ? (
        <Link href={FORM_REGISTER_URL} target="_blank" rel="noopener noreferrer">
          {displayText}
        </Link>
      ) : (
        <Link href={isCourseEnrolled ? '/my-courses' : `/buy-now?course=mlops-1`} rel="noopener noreferrer">
          {isCourseEnrolled ? text.MAIN.INTRODUCTION.HEADING_6 : text.MAIN.INTRODUCTION.HEADING_5}
        </Link>
      )}
    </Button>
  )
}
